/**
 * ניתוח ביצועים — the PDF's return/risk section: Sharpe table for the last
 * 12/24/36 months + a return-vs-stddev scatter (36m). Computed from the
 * per-fund monthly returns (workbook months as the workbook has them, statement
 * months as withStatementReturns works them out). Sharpe uses rf=0 (labeled).
 */
import { CartesianGrid, LabelList, ResponsiveContainer, Scatter, ScatterChart, Tooltip, XAxis, YAxis } from 'recharts';
import { Card, EmptyState } from './Card.js';
import { FundLogo } from './FundChip.js';
import { fmtNum, fmtPct, ltrNum } from '../utils.js';
import { AXIS, GRID, SERIES, TOOLTIP_PROPS, PIE_COLORS } from '../chartTheme.js';
import type { PerformanceRow } from '@agio/core';

interface WindowStat {
  fund: string;
  ret: number;
  std: number;
  sharpe: number;
  months: number;
}

function windowStats(perf: PerformanceRow[], months: number): WindowStat[] {
  const last = perf.reduce<string | null>((a, p) => (!a || p.date > a ? p.date : a), null);
  if (!last) return [];
  const ym = last.slice(0, 7);
  const byFund = new Map<string, PerformanceRow[]>();
  for (const p of perf) {
    if (p.monthly_return === null) continue;
    if (!byFund.has(p.fund_id)) byFund.set(p.fund_id, []);
    byFund.get(p.fund_id)!.push(p);
  }
  const out: WindowStat[] = [];
  for (const rows of byFund.values()) {
    const sorted = rows.sort((a, b) => a.date.localeCompare(b.date));
    if (sorted.at(-1)?.date.slice(0, 7) !== ym) continue; // active funds only
    const win = sorted.slice(-months);
    if (win.length < months) continue; // full window required (matches the PDF footnote)
    const rets = win.map((r) => r.monthly_return as number);
    const cum = rets.reduce((a, r) => a * (1 + r), 1) - 1;
    const mean = rets.reduce((a, r) => a + r, 0) / rets.length;
    const std = Math.sqrt(rets.reduce((a, r) => a + (r - mean) ** 2, 0) / (rets.length - 1));
    out.push({ fund: win[0].fund, ret: cum, std, sharpe: std > 0 ? cum / (std * Math.sqrt(12)) : 0, months });
  }
  return out.sort((a, b) => a.fund.localeCompare(b.fund));
}

/** Data range of a numeric key — 0 when all points coincide. */
function span(rows: Record<string, unknown>[], key: string): number {
  const vs = rows.map((r) => r[key] as number);
  return vs.length ? Math.max(...vs) - Math.min(...vs) : 0;
}

export function RiskAnalysis({ perf }: { perf: PerformanceRow[] }) {
  const windows = [12, 24, 36].map((m) => ({ months: m, stats: windowStats(perf, m) }));
  const scatter = windows[2].stats.map((s, i) => ({ ...s, retPct: s.ret * 100, stdPct: s.std * 100, fill: PIE_COLORS[i % PIE_COLORS.length] }));
  if (windows.every((w) => w.stats.length === 0)) {
    return <Card title="ניתוח ביצועים — פיזור תשואה/סיכון"><EmptyState label="נדרשת היסטוריה של 12 חודשים לפחות" /></Card>;
  }

  return (
    <Card title="ניתוח ביצועים — פיזור תשואה/סיכון">
      <div className="mb-6 grid gap-6 lg:grid-cols-3">
        {windows.map(({ months, stats }) => (
          <div key={months}>
            <h3 className="mb-2 text-sm font-semibold text-muted-foreground">{months} חודשים</h3>
            {stats.length === 0 ? (
              <p className="text-xs text-muted-foreground">אין בתי השקעות עם היסטוריה מלאה</p>
            ) : (
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-border text-right text-muted-foreground">
                    <th className="py-1.5 font-normal">ישות</th>
                    <th className="py-1.5 font-normal">תשואה</th>
                    <th className="py-1.5 font-normal">סטיית תקן</th>
                    <th className="py-1.5 font-normal">שארפ*</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.map((s) => (
                    <tr key={s.fund} className="border-b border-border/40">
                      <td className="flex items-center gap-1.5 py-1.5">
                        <FundLogo name={s.fund} size={14} />
                        {s.fund}
                      </td>
                      <td className="py-1.5 tabular-nums">{fmtPct(s.ret)}</td>
                      <td className="py-1.5 tabular-nums text-muted-foreground">{fmtPct(s.std)}</td>
                      <td className="py-1.5 tabular-nums">{fmtNum(s.sharpe, 2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ))}
      </div>

      {scatter.length > 0 && (
        <ResponsiveContainer width="100%" height={280}>
          <ScatterChart margin={{ top: 20, right: 20, bottom: 10, left: 10 }}>
            <CartesianGrid {...GRID} />
            {/* Padded domains so the points spread instead of bunching in a corner. */}
            <XAxis
              type="number" dataKey="stdPct" name="סטיית תקן" {...AXIS}
              domain={[(min: number) => Math.max(0, min - (span(scatter, 'stdPct') || min) * 0.25), (max: number) => max + (span(scatter, 'stdPct') || max) * 0.25]}
              tickFormatter={(v: number) => ltrNum(`${v.toFixed(2)}%`)}
            />
            <YAxis
              type="number" dataKey="retPct" name="תשואה" orientation="right" {...AXIS}
              domain={[(min: number) => min - (span(scatter, 'retPct') || Math.abs(min)) * 0.3, (max: number) => max + (span(scatter, 'retPct') || max) * 0.3]}
              tickFormatter={(v: number) => ltrNum(`${v.toFixed(1)}%`)}
            />
            <Tooltip
              {...TOOLTIP_PROPS}
              formatter={(v: number, name: string) => [ltrNum(`${v.toFixed(2)}%`), name === 'retPct' ? 'תשואה' : 'סטיית תקן']}
              labelFormatter={() => ''}
            />
            <Scatter data={scatter} fill={SERIES.portfolio}>
              <LabelList dataKey="fund" position="top" style={{ fill: '#C9D6E3', fontSize: 11 }} />
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>
      )}
      <p className="mt-2 text-xs text-muted-foreground">
        * שארפ מחושב עם ריבית חסרת סיכון 0; תשואה מצטברת לחלון המצוין; רק בתי השקעות עם היסטוריה מלאה לחלון נכללים.
      </p>
    </Card>
  );
}
